(ns minorodata.recipes.subs
  (:require [clojure.string :as str]
            [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]))


(defn input-placeholder-vector
  "Filters out dublicate inputs and adds separators"
  [templates]
  (let [blocks (map-indexed
                (fn [idx [template-name {:keys [input-tables output-tables]}]]
                  (concat [{:type :subtitle :content (str "Configuring template " (name template-name))}]
                          input-tables
                          output-tables))
                templates)]
    (distinct (apply concat blocks))))


(defn input-field->form-blocks [{:keys [type content identifier origin meta]}]
  ;; Use schema-registry definition for validation
  (case type

    ;; These weird custom types are needed so we can interleave each template
    ;; inputs section with a template-specific title or a separator
    :hr        [{:content [:hr {:width "100%"}]}]
    :subtitle  [{:block-type type :content content}]

    (if (some? origin)
      (case (:type origin)
        "extract" (let [source-name (keyword (str identifier "-source"))]
                    [{:block-type  :recipe-source-select
                      :name        source-name
                      :required    true
                      :source-type (:name origin)

                      :upstream-deps   []
                      :downstream-deps []}

                     {:block-type :recipe-extract-select
                      :name       (keyword identifier)
                      :required   true
                      :source-id   [source-name :value]

                      :upstream-deps   [source-name]
                      :downstream-deps []}])

        "group"   [{:block-type :recipe-group-select
                    :name        (keyword identifier)
                    :required    true}]

        ;; default fallback when unsupported origin type is encountered
        [{:content (str "Unsupported input origin type: " origin ".")}])

      ;; When no :origin is provided we treat the field as
      ;; a simple input
      [(merge {:block-type :input
               :name       (keyword identifier)
               :label      "Output table"
               :required   true
               :value      ""}
              meta)])))


(defn compile-to-inputs
  "Compiles templates field definitions into input definitions blocks.

  Example templates map:
  ```
  {:template-id {:input-tables [{:identifier \"id1\"}]
                 :output-tables [{:identifier \"id2\"}]}}
  ```

  `:input-tables` and `:output-tables` vectors contain field definition maps.

  Every field definition map is expected to have at least an `:identifier`.
  Optional `:origin` field may contain origin definition map.
  Accepted origins:
    * `extract` — generates source and extract select block for the source
      type given in `:name`. Will save extract id as field value.
    * `group` — generates group select gropdown. Resulting value is group id.
    * anything else — just a simple text input field.
  `:meta` field contains optional field title and other input related parameters.

  All fields are required by default."
  [templates]
  (let [input-definitions (input-placeholder-vector templates)

        recipe-blocks (vec (mapcat input-field->form-blocks input-definitions))

        prefix-blocks [{:content "Select the group you want the transforms to be created in"}

                       {:block-type :recipe-group-select
                        :name       :group-id
                        :required   true}]

        suffix-blocks [{:block-type :submit :content "DEPLOY"}]]

    (concat prefix-blocks
            recipe-blocks
            suffix-blocks)))


(defn compile-value-map
  "Generates a mapping of values to input form fields that the api is
  expecting to receive for the given templates array.

  Refer to `compile-inputs` function for templates array documentation."
  [templates]
  (reduce (fn [acc [template-id template-fields]]
            (let [{:keys [input-tables output-tables]} template-fields
                  fields (concat input-tables output-tables)

                  associations (map (fn [field]
                                      (let [field-id (:identifier field)]
                                        [[template-id field-id] (keyword field-id)]))
                                    fields)

                  field-associations (into {} associations)]
              (merge acc field-associations)))
          {}
          templates))


(defn assign-api-values
  "Selects only the needed values from the form inputs map and arranges them
  in a shape that api expects based on recipe template definitions."
  [templates data]
  (let [input-map (compile-value-map templates)]
    (reduce (fn [api-data [path data-key]]
              (assoc-in api-data path (get data data-key)))
            {}
            input-map)))


(defn make-form-definition [recipe]
  (let [{:recipe/keys [templates recipe-id]} recipe

        form-id    (keyword (str recipe-id "-form"))
        blocks     (compile-to-inputs templates)

        blocks     []]

    {:format-for-api
     (fn [data]
       {:group-id        (:group-id data)
        :template-inputs (assign-api-values templates data)})

     :format-for-inputs
     (fn [_]
       {})

     :validate (constantly nil)

     :id form-id
     :blocks blocks}))


(defn augment-recipe [recipe]
  (let [form-definition (make-form-definition recipe)]

    (assoc recipe :recipe/form-definition form-definition)))


(re-frame/reg-sub
 :recipes.subs/recipes
 (fn [_ _]
   [(re-posh/subscribe [:recipe/pull-many])])
 (fn [[recipes]]
   (map augment-recipe recipes)))


(re-frame/reg-sub
 :recipes.subs/source-options
 (fn [_ [_ source-type]]
   (let [sources @(re-posh/subscribe [:source/pull-many])]
     (filter #(= source-type (:source/source-type %)) sources))))


(re-frame/reg-sub
 :recipes.subs/extract-options
 (fn [_ [_ source-id]]
   (let [{:keys [extracts]} @(re-frame/subscribe [:connections.subs/extracts-for-source source-id])]
     extracts)))


(re-frame/reg-sub
 :recipes.subs/group-options
 (fn [_ [_ _]]
   (let [groups @(re-posh/subscribe [:group/pull-many])]
     groups)))

(re-frame/reg-sub
 :recipes.subs/recipes-for-type
 (fn [_ [_ source-type]]
   (let [recipes @(re-frame/subscribe [:recipes.subs/recipes])]
     (filter (fn [r]
               (when (:recipe/enabled r)
                 (let [types (->> r :recipe/source-report-platform-type
                                  (filter identity)
                                  (map #(first (clojure.string/split % #",")))
                                  set)]
                   (get types source-type))))
             recipes))))

(re-frame/reg-sub
 :recipes.subs/has-available-recipes-for-type
 (fn [_ [_ source-type]]
   false
   ;; Disable recipes as the service is failing
   #_(let [recipes @(re-frame/subscribe [:recipes.subs/recipes-for-type source-type])]
     (and @(re-posh/subscribe [:app.subs/enabled-for-bigquery :recipes])
          (-> recipes count pos?)))))


(re-frame/reg-sub
 :recipes.subs/mini-dag
 (fn [db [_ recipe-id source-id]]
   (get-in db [:recipe-mini-dags recipe-id source-id])))
